/**
 * `<ChangeReviewPanel/>` — the shared review screen (#1961, slice 2D of
 * #1948; layout confirmed by the founder: "Layout as proposed makes sense").
 *
 * **Why the lead comes:** a draft wants to change the live roster and they
 * decide what lands. **What they see first:** the people who move, one row
 * each — before slots on the left, after slots on the right, a vacated cell
 * struck through, a filled cell outlined in the accent. **What they adjust
 * straight away:** reject a row, or hand a filled seat to one of the
 * ladder's candidates ("Use Hana instead"), then one Apply.
 *
 * Used by *Fix and re-solve* (the repair draft, `/repair/{id}/apply`) and
 * the Corrections panel (absorb / combine drafts, `corrections/apply`) and the
 * cross-tier knock-on (#1964, `<SeatKnockOnHost/>`, `shortfalls/seat/apply`). The caller owns the apply call:
 * `onApply` receives the decision (cells to leave alone, seats handed to
 * someone else) and returns its promise. A 409 — the roster or a pin moved
 * since the draft — lists the cells it names in red under the button.
 *
 * Content only: the caller frames it (the repair in the right-panel overlay,
 * the corrections draft inline where its propose controls are). Copy is
 * names, dates and cells — no narration.
 */
import { QueryClientContext } from "@tanstack/react-query";
import { useContext, useMemo, useState } from "react";

import { ApiError } from "../../api/client";
import { invalidateAfterUnpin, releaseReviewPins } from "../../api/leadFixedUnpin";
import {
  type ConflictCell,
  type ReviewAlternative,
  type ReviewChange,
  type ReviewDecision,
  type ReviewPin,
  type ReviewRow,
  type ReviewSlot,
  conflictCells,
  groupChangesByPerson,
  reviewDate,
  reviewDecision,
  reviewSession,
} from "../../lib/changeReview";
import { cn } from "../../lib/cn";
import { useDemoLocked } from "../../lib/demoLock";
import { Button } from "../ui";
import { ReviewPinControl, ReviewUnpinContext } from "./ReviewPinControl";

export interface ChangeReviewPanelProps {
  changes: readonly ReviewChange[];
  pins?: readonly ReviewPin[] | undefined;
  /** Writes the decision. Rejects with the API error on a refusal. */
  onApply: (decision: ReviewDecision) => Promise<unknown>;
  /** Shown beside Apply when given. */
  onDiscard?: (() => void) | undefined;
  /** Text of a refusal that is not a 409. Defaults to the API's detail. */
  describeError?: ((err: unknown) => string) | undefined;
  /** This member's row is listed first (the cross-tier knock-on puts the
   *  person the lead picked above the backfill their move needs). */
  firstMembershipId?: string | undefined;
  testid?: string | undefined;
}

function slotText(slot: ReviewSlot): string {
  return `${reviewDate(slot.date)} · ${reviewSession(slot.session)} · ${slot.station}`;
}

function Slot({ slot, testid }: { slot: ReviewSlot; testid: string }) {
  return (
    <li
      data-testid={testid}
      data-slot={slot.kind}
      className={cn(
        "rounded-sm border px-1.5 py-0.5 text-[11px] leading-tight",
        slot.kind === "vacated"
          ? "border-dashed border-border-strong text-fg-subtle line-through"
          : "border-accent bg-accent-soft/30 text-fg",
      )}
    >
      {slotText(slot)}
    </li>
  );
}

/** Candidates listed per filled seat, in the ladder's order; the click-through
 *  on a 90-person team listed nine under each row. */
const ALTERNATIVES_SHOWN = 3;

function defaultError(err: unknown): string {
  if (err instanceof ApiError) return err.detail;
  return err instanceof Error ? err.message : "Not applied.";
}

export function ChangeReviewPanel({
  changes,
  pins = [],
  onApply,
  onDiscard,
  describeError = defaultError,
  firstMembershipId,
  testid = "change-review",
}: ChangeReviewPanelProps) {
  const [swaps, setSwaps] = useState<Record<string, ReviewAlternative>>({});
  const [rejected, setRejected] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const [conflict, setConflict] = useState<ConflictCell[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // #1988 — pinned rows the lead unpinned: applied as placed, not kept pinned.
  const unpinScope = useContext(ReviewUnpinContext);
  // #2824 - the demo releases no seat.
  const demoLocked = useDemoLocked();
  const queryClient = useContext(QueryClientContext);
  const [unpinned, setUnpinned] = useState<Set<string>>(new Set());

  const rows = useMemo(() => {
    const grouped = groupChangesByPerson(changes, pins, swaps);
    if (!firstMembershipId) return grouped;
    return [
      ...grouped.filter((r) => r.key === firstMembershipId),
      ...grouped.filter((r) => r.key !== firstMembershipId),
    ];
  }, [changes, pins, swaps, firstMembershipId]);
  const decision = useMemo(() => reviewDecision(rows, rejected), [rows, rejected]);
  const names = useMemo(() => {
    const out = new Map<string, string>();
    for (const c of changes) out.set(c.membership_id, c.display_name);
    for (const r of rows) out.set(r.membership_id, r.display_name);
    return out;
  }, [changes, rows]);

  const toggle = (row: ReviewRow) => {
    setRejected((prev) => {
      const next = new Set(prev);
      if (next.has(row.key)) next.delete(row.key);
      else next.add(row.key);
      return next;
    });
  };

  const apply = async () => {
    setApplying(true);
    setConflict(null);
    setError(null);
    try {
      await onApply(decision);
      const release = rows
        .filter((r) => unpinned.has(r.key))
        .flatMap((r) => (r.pins ?? []).filter((p) => !p.off && p.station));
      if (unpinScope && release.length > 0) {
        try {
          const touched = await releaseReviewPins(unpinScope, release);
          invalidateAfterUnpin(queryClient, [unpinScope.cycleId, ...touched]);
        } catch (err) {
          setError(
            `Applied · a pin was not removed (${describeError(err)}); unpin it on the roster`,
          );
        }
      }
    } catch (err) {
      const cells = conflictCells(err);
      if (cells) setConflict(cells);
      else setError(describeError(err));
    } finally {
      setApplying(false);
    }
  };

  const n = decision.count;
  return (
    <div data-testid={testid} className="space-y-3 text-sm">
      {rows.length === 0 ? (
        <p data-testid={`${testid}-empty`} className="text-xs text-fg-muted">
          No changes.
        </p>
      ) : (
        <ul className="space-y-2">
          <li
            aria-hidden="true"
            className="grid grid-cols-2 gap-2 px-2.5 text-[10px] font-semibold uppercase tracking-wide text-fg-faint"
          >
            <span>Before</span>
            <span>After</span>
          </li>
          {rows.map((row) => {
            const isRejected = rejected.has(row.key) && !row.pinned;
            return (
              <li
                key={row.key}
                data-testid={`${testid}-row-${row.membership_id}`}
                data-rejected={isRejected ? "true" : "false"}
                className={cn(
                  "space-y-1.5 rounded-sm border border-border px-2.5 py-2",
                  isRejected ? "bg-bg-subtle opacity-60" : "bg-bg-elevated",
                )}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="min-w-0 flex-1 truncate text-xs font-medium text-fg">
                    {row.display_name}
                    {row.insteadOf ? (
                      <span className="font-normal text-fg-subtle">
                        {" "}
                        · for {row.insteadOf.display_name}
                      </span>
                    ) : null}
                  </p>
                  {row.pinned ? (
                    <ReviewPinControl
                      name={row.display_name}
                      canUnpin={
                        !demoLocked &&
                        Boolean(unpinScope) &&
                        (row.pins ?? []).some((p) => !p.off && Boolean(p.station))
                      }
                      unpinned={unpinned.has(row.key)}
                      onUnpin={() => setUnpinned((prev) => new Set(prev).add(row.key))}
                      onRestore={() =>
                        setUnpinned((prev) => {
                          const next = new Set(prev);
                          next.delete(row.key);
                          return next;
                        })
                      }
                      testid={`${testid}-pin-${row.membership_id}`}
                    />
                  ) : row.insteadOf ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2"
                      data-testid={`${testid}-unswap-${row.membership_id}`}
                      onClick={() => {
                        const key = row.insteadOf?.changeKey;
                        if (!key) return;
                        setSwaps((prev) => {
                          const next = { ...prev };
                          delete next[key];
                          return next;
                        });
                      }}
                    >
                      Keep {row.insteadOf.display_name}
                    </Button>
                  ) : (
                    <fieldset
                      aria-label={`${row.display_name}: accept or reject`}
                      className="flex shrink-0 overflow-hidden rounded-sm border border-border"
                    >
                      <button
                        type="button"
                        aria-pressed={!isRejected}
                        data-testid={`${testid}-accept-${row.membership_id}`}
                        onClick={() => isRejected && toggle(row)}
                        className={cn(
                          "px-2 py-0.5 text-[11px]",
                          !isRejected ? "bg-accent-soft/60 text-fg" : "text-fg-muted",
                        )}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        aria-pressed={isRejected}
                        data-testid={`${testid}-reject-${row.membership_id}`}
                        onClick={() => !isRejected && toggle(row)}
                        className={cn(
                          "border-l border-border px-2 py-0.5 text-[11px]",
                          isRejected ? "bg-bg-subtle text-fg" : "text-fg-muted",
                        )}
                      >
                        Reject
                      </button>
                    </fieldset>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <ul className="space-y-1" aria-label="Before">
                    {row.before.length === 0 ? (
                      <li className="text-[11px] text-fg-faint">—</li>
                    ) : (
                      row.before.map((s) => (
                        <Slot
                          key={`b-${s.date}-${s.session}-${s.station}`}
                          slot={s}
                          testid={`${testid}-before-${row.membership_id}`}
                        />
                      ))
                    )}
                  </ul>
                  <ul className="space-y-1" aria-label="After">
                    {row.after.length === 0 ? (
                      <li className="text-[11px] text-fg-faint">Off</li>
                    ) : (
                      row.after.map((s) => (
                        <Slot
                          key={`a-${s.date}-${s.session}-${s.station}`}
                          slot={s}
                          testid={`${testid}-after-${row.membership_id}`}
                        />
                      ))
                    )}
                  </ul>
                </div>
                {!isRejected && row.options.length > 0 ? (
                  <ul
                    className="space-y-1 border-t border-border pt-1.5"
                    data-testid={`${testid}-alternatives-${row.membership_id}`}
                  >
                    {row.options.flatMap((opt) =>
                      opt.alternatives.slice(0, ALTERNATIVES_SHOWN).map((alt) => (
                        <li
                          key={`${opt.changeKey}-${alt.membership_id}`}
                          className="flex items-center justify-between gap-2 text-[11px]"
                        >
                          <span className="min-w-0 truncate text-fg-muted">
                            {alt.display_name}
                            {alt.reason ? ` · ${alt.reason}` : ""}
                            {row.options.length > 1 ? ` · ${opt.slot.station}` : ""}
                          </span>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 shrink-0 px-2"
                            data-testid={`${testid}-use-${alt.membership_id}`}
                            onClick={() => setSwaps((prev) => ({ ...prev, [opt.changeKey]: alt }))}
                          >
                            Use {alt.display_name} instead
                          </Button>
                        </li>
                      )),
                    )}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          data-testid={`${testid}-apply`}
          disabled={applying || n === 0}
          onClick={() => void apply()}
        >
          {applying ? "Applying…" : `Apply ${n} change${n === 1 ? "" : "s"}`}
        </Button>
        {onDiscard ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            data-testid={`${testid}-discard`}
            disabled={applying}
            onClick={onDiscard}
          >
            Discard
          </Button>
        ) : null}
      </div>
      {conflict ? (
        <div role="alert" data-testid={`${testid}-conflict`} className="space-y-1">
          <p className="text-xs font-medium text-danger">Changed since you looked · run again</p>
          {conflict.length > 0 ? (
            <ul className="space-y-0.5">
              {conflict.map((c) => (
                <li
                  key={`${c.membership_id}-${c.date}-${c.station ?? ""}-${c.session ?? ""}`}
                  data-testid={`${testid}-conflict-cell`}
                  className="text-[11px] text-danger"
                >
                  {names.get(c.membership_id) ?? "Member"} · {reviewDate(c.date)}
                  {c.session ? ` · ${reviewSession(c.session)}` : ""}
                  {c.station ? ` · ${c.station}` : ""}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <p role="alert" data-testid={`${testid}-apply-error`} className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export default ChangeReviewPanel;
